"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getQuestionById, type WeightCategory } from "@/lib/questions";
import {
  getUniversityLinkQuestion,
  universityLinkQuestionTtsPath,
} from "@/lib/universityLink/api";

// 練習画面が扱う質問の形。共通質問（静的定義）と大学質問（API）のどちらから来ても同じ形にする。
export interface PracticeQuestion {
  text: string;
  /** 共通質問マスターの ID。大学質問のときは無い */
  presetId?: number;
  /** 大学が登録した質問の ID。共通質問のときは無い（#1861） */
  universityQuestionId?: string;
  /** 採点の配点を切り替えるカテゴリ。大学質問は持たず、既定の配点で採点する */
  weightCategory?: WeightCategory;
  hint?: string;
  hint_new_grad?: string;
  hint_high_school?: string;
  /**
   * 回答送信（POST /answers）に添える、出題元を示すパラメータ。
   * 共通質問は question_id と question_category、大学質問は university_question_id だけを持つ。
   * 音声（FormData）とテキスト（JSON）の送信が同じ組を使う。
   */
  answerParams: Record<string, string>;
  /** 読み上げを生成する経路の上書き。共通質問は無い（質問 ID から組み立てる） */
  ttsGeneratePath?: string;
}

/** unauthorized は未ログインで大学質問を開いたとき（ログインを促す） */
export type PracticeQuestionStatus = "loading" | "ready" | "not_found" | "unauthorized" | "error";

export interface UsePracticeQuestionResult {
  status: PracticeQuestionStatus;
  /** ready 以外のときは本文が空の仮の値。呼び出し側は status を見て描画を分ける */
  question: PracticeQuestion;
  /** error のときに取り直す */
  retry: () => void;
}

interface SearchParamsLike {
  get(name: string): string | null;
}

const EMPTY_QUESTION: PracticeQuestion = { text: "", answerParams: {} };

function presetQuestion(presetId: string | null): PracticeQuestion {
  const preset = getQuestionById(Number(presetId ?? "1"));
  return {
    text: preset.text,
    presetId: preset.id,
    weightCategory: preset.weight_category,
    hint: preset.hint,
    hint_new_grad: preset.hint_new_grad,
    hint_high_school: preset.hint_high_school,
    answerParams: {
      question_id: String(preset.id),
      question_category: preset.weight_category,
    },
  };
}

// 大学質問は共通マスターの ID とカテゴリを持たない。出題元を university_question_id で記録し、
// 採点は既定の配点になる（#1861）
function universityQuestion(id: string, text: string): PracticeQuestion {
  return {
    text,
    universityQuestionId: id,
    answerParams: { university_question_id: id },
    ttsGeneratePath: universityLinkQuestionTtsPath(id),
  };
}

/**
 * URL から練習する質問を解決する。
 *
 * - `?universityQuestionId=<uuid>`: 大学が登録した質問。API から1件取る（#1861）
 * - `?questionId=<数値>`: 共通質問。静的定義から引く（従来どおり、同期で ready）
 *
 * 両方あるときは大学質問を優先する。404 は not_found、それ以外の失敗は error として区別する
 * （削除された質問と一時的な障害で、案内と取れる行動が違う）。
 */
export function usePracticeQuestion(searchParams: SearchParamsLike): UsePracticeQuestionResult {
  const universityQuestionId = searchParams.get("universityQuestionId");
  const presetId = searchParams.get("questionId");
  // 描画ごとに新しいオブジェクトを返すと、これを依存に持つ呼び出し側のメモ化が毎回外れる
  const preset = useMemo(() => presetQuestion(presetId), [presetId]);
  const [attempt, setAttempt] = useState(0);
  // 取得結果。どの ID・何回目の試行に対する結果かを持ち、一致しない間は読み込み中として扱う
  // （効果の中で同期的に loading へ更新すると再描画が連鎖するため、状態を持たず導出する）。
  const [remote, setRemote] = useState<{
    id: string;
    attempt: number;
    status: PracticeQuestionStatus;
    question: PracticeQuestion;
  } | null>(null);

  useEffect(() => {
    if (!universityQuestionId) return;
    let cancelled = false;
    getUniversityLinkQuestion(universityQuestionId)
      .then((data) => {
        if (cancelled) return;
        setRemote({
          id: universityQuestionId,
          attempt,
          status: "ready",
          question: universityQuestion(data.id, data.text),
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const httpStatus = axios.isAxiosError(error) ? error.response?.status : undefined;
        const status: PracticeQuestionStatus =
          httpStatus === 404 ? "not_found" : httpStatus === 401 ? "unauthorized" : "error";
        setRemote({ id: universityQuestionId, attempt, status, question: EMPTY_QUESTION });
      });
    return () => {
      cancelled = true;
    };
  }, [universityQuestionId, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  if (!universityQuestionId) {
    return { status: "ready", question: preset, retry };
  }

  // 取得が済む前の描画、別の ID へ遷移した直後、再試行の直後は読み込み中として扱う
  if (!remote || remote.id !== universityQuestionId || remote.attempt !== attempt) {
    return { status: "loading", question: EMPTY_QUESTION, retry };
  }
  return { status: remote.status, question: remote.question, retry };
}
